import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import clsx from 'clsx';
import { Button, TextField, Typography, Link, Checkbox } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';

// CONSTANTS
import { ROUTE_PRIVACY_POLICY, ROUTE_TERMS_AND_CONDITIONS } from '@/constants/routes';

// UTILITY
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';
import { analyticsIdentify, analyticsCreatedSubscriptionAccount } from '@/utility/analytics';
import { getSearchParams } from '@/utility/common';

// COMPONENTS
import PasswordInput from '@/components/Form/PasswordInput';
import RouterLink from '@/components/Link';

// HELP
import ZipCodeInput from './help/ZipCodeInput';

// STYLES
import { SRegistrationForm, TermsContainer } from './styles';

function getRegistrationData(values, dictionary) {
  const {
    subscription: { merchant },
  } = dictionary;

  const type = merchant.slug;

  return {
    ...values,
    type,
  };
}

function RegistrationForm(props) {
  const {
    dictionary,
    onNext,
    addSubscriptionRegistration,
    loading,
    user,
    analyticsLocation,
    manageAuthDialogState,
    partner,
    variant,
    isDialog,
    disabled,
  } = props;
  const [t] = useTranslation();
  const { slug } = useParams();
  const { search } = useLocation();
  const params = getSearchParams(search);
  const zipCodeParam = params.get('zipcode');
  const {
    subscription: {
      registration_meta: { account_fields },
    },
    app,
    selectedProduct,
    selectedPlan,
    selectedAddons,
    app_account,
    app_account_temp,
    totalPrice,
  } = dictionary;
  const isLogin = variant === 'login';

  useEffect(() => {
    if (!isDialog && app_account_temp && !app_account) onNext();
  }, []);

  const labels = {
    email: t('inputs.emailAddress'),
    password: t('inputs.password'),
    zip: t('inputs.zipcode'),
  };

  function onSubmit(values) {
    const registrationData = getRegistrationData(values, dictionary);
    const addons = selectedAddons
      ? selectedAddons.map(addon => ({
          addon_id: addon.id,
          addon_plan_id: addon.plans[0].id,
        }))
      : [];

    function onSuccessCallback() {
      const { email, zip } = registrationData;

      analyticsIdentify(email, {
        action: 'Create Account in Subscription',
        app: slug,
        email,
      });

      analyticsCreatedSubscriptionAccount({
        location: analyticsLocation,
        app_id: app.id,
        product_id: selectedProduct.id,
        plan_id: selectedPlan.id,
        addons,
        email,
        zip_code: zip,
        price: totalPrice,
      });

      onNext();
    }

    const data = {
      ...registrationData,
      plan_id: selectedPlan.id,
      partner_id: partner?.partner_id || null,
    };

    addSubscriptionRegistration({
      registrationData: data,
      onError: error => {
        const { email } = data;

        if (error.response?.data.status === 'incorrect_password') {
          manageAuthDialogState({
            skip_app_onboarding: true,
            profDrawerState: false,
            screen: 'login',
            loginTitle: t('subscription.mbLogin.title'),
            loginDescription: t('subscription.mbLogin.desc', {
              app: app.name,
            }),
            email,
            callback: () => {
              addSubscriptionRegistration({
                registrationData: data,
                callback: onSuccessCallback,
              });
            },
          });
        }
      },
      callback: onSuccessCallback,
    });
  }

  const initialValues = account_fields.reduce(
    (currValues, { name }) => {
      if (name === 'email' && user?.email) currValues[name] = user.email;
      if (name === 'zip' && zipCodeParam) currValues[name] = zipCodeParam;

      if (isLogin) {
        const appAcc = app_account_temp?.payload || app_account;

        if (appAcc) {
          currValues[name] = (name === 'zip' ? appAcc.zipcode : appAcc[name]) || '';
        }
      }

      return currValues;
    },
    isLogin ? {} : { terms: true },
  );
  const fields = isLogin
    ? account_fields
    : [...account_fields, { name: 'terms', type: 'checkbox' }];
  const validationFields = isLogin
    ? account_fields
    : [...account_fields, { name: 'terms', required: true, type: 'boolean' }];
  const disabledSubmitBtn = disabled || loading;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={setYupValidationSchema('subscriptionRegistrationSchema', t, null, {
        account_fields: validationFields,
      })}
      validateOnMount
    >
      {({ isValid, values }) => {
        return (
          <SRegistrationForm id="my-form" className={clsx(isDialog && 'form--dialog')}>
            <input type="email" name="email" style={{ display: 'none' }} />
            <input type="password" name="password" style={{ display: 'none' }} />
            <input type="tel" name="zip" style={{ display: 'none' }} />

            {fields.map(({ name, type }) => {
              return (
                <Field key={name} name={name}>
                  {({ field, meta: { touched, error } }) => {
                    const fieldProps = {
                      size: 'small',
                      name: field.name,
                      type,
                      value: field.value,
                      onChange: field.onChange,
                      onBlur: field.onBlur,
                    };

                    const inputProps = {
                      ...fieldProps,
                      className: 'm-b-15',
                      label: labels[name],
                      autoComplete: `new-${field.name}`,
                      error: touched && !!error,
                      helperText: touched && error,
                    };

                    const checkboxProps = {
                      ...fieldProps,
                      checked: values[name],
                    };

                    if (type === 'password') {
                      return <PasswordInput {...inputProps} />;
                    }

                    if (name === 'zip') {
                      return <ZipCodeInput {...inputProps} />;
                    }

                    if (name === 'terms') {
                      return (
                        <TermsContainer>
                          <Checkbox {...checkboxProps} />
                          <Typography className="p5">
                            {t('authDrawer.registration.termsMiddle')}{' '}
                            <Link
                              className="p5"
                              component={RouterLink}
                              to={ROUTE_TERMS_AND_CONDITIONS}
                              variant="body1"
                              target="_blank"
                              underline="always"
                            >
                              {t('buttons.termsOfService')}
                            </Link>
                            &nbsp;
                            {t('common.and')}{' '}
                            <Link
                              className="p5"
                              component={RouterLink}
                              to={ROUTE_PRIVACY_POLICY}
                              variant="body1"
                              target="_blank"
                              underline="always"
                            >
                              {t('buttons.privacyPolicy')}
                            </Link>
                            .
                          </Typography>
                        </TermsContainer>
                      );
                    }

                    return <TextField {...inputProps} />;
                  }}
                </Field>
              );
            })}

            <Button
              type="submit"
              disabled={!isValid || disabledSubmitBtn}
              color="primary"
              fullWidth
            >
              {t(`buttons.${isLogin ? 'login' : 'createAccount'}`)}
            </Button>
          </SRegistrationForm>
        );
      }}
    </Formik>
  );
}

RegistrationForm.defaultProps = {
  isDialog: false,
  loading: false,
  variant: 'register', // login | register
};

RegistrationForm.propTypes = {
  user: PropTypes.object,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  dictionary: PropTypes.object.isRequired,
  addSubscriptionRegistration: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  analyticsLocation: PropTypes.string.isRequired,
  variant: PropTypes.string,
  manageAuthDialogState: PropTypes.func.isRequired,
  partner: PropTypes.object,
  isDialog: PropTypes.bool,
};

export default RegistrationForm;
